import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Coins, Loader2, Download, Banknote } from 'lucide-react';

const inr = (v) => `₹${(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

export default function AdminResellerCommissionsPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ commissions: [], totals: {} });
  const [resellers, setResellers] = useState([]);
  const [fReseller, setFReseller] = useState('all');
  const [fStatus, setFStatus] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fReseller !== 'all') params.append('reseller_id', fReseller);
      if (fStatus !== 'all') params.append('status', fStatus);
      const [c, r] = await Promise.all([
        api.get(`/admin/reseller/commissions?${params.toString()}`),
        resellers.length ? Promise.resolve({ data: { resellers } }) : api.get('/admin/resellers'),
      ]);
      setData(c.data);
      if (!resellers.length) setResellers(r.data.resellers || []);
    } finally { setLoading(false); }
  }, [api, fReseller, fStatus, resellers]);
  useEffect(() => { load(); }, [fReseller, fStatus]); // eslint-disable-line

  const exportFile = async (format) => {
    try {
      const params = new URLSearchParams({ format });
      if (fReseller !== 'all') params.append('reseller_id', fReseller);
      if (fStatus !== 'all') params.append('status', fStatus);
      const res = await api.get(`/admin/reseller/commissions/export?${params.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `reseller_commissions.${format}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const settle = async () => {
    if (fReseller === 'all') { toast.error('Select a reseller to settle'); return; }
    try { const r = await api.post('/admin/reseller/settlements/mark-paid', { reseller_id: fReseller });
      toast.success(r.data.message); await load(); } catch { toast.error('Settlement failed'); }
  };

  const t = data.totals || {};

  return (
    <div className="space-y-6" data-testid="admin-reseller-commissions-page">
      <div>
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><Coins className="w-6 h-6" />Reseller Commissions</h1>
        <p className="text-muted-foreground">Commission ledger across all partners.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[['Gross', t.gross], ['Reversals', t.reversals], ['Paid', t.paid], ['Pending', t.pending]].map(([l, v]) => (
          <Card key={l}><CardContent className="p-5"><p className="text-xs text-muted-foreground mb-1">{l}</p><p className="text-2xl font-bold font-mono">{inr(v)}</p></CardContent></Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <Select value={fReseller} onValueChange={setFReseller}>
          <SelectTrigger className="w-56" data-testid="filter-reseller"><SelectValue placeholder="Reseller" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All resellers</SelectItem>
            {resellers.map((r) => <SelectItem key={r.id} value={r.id}>{r.business_name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="w-44" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>{['all', 'pending', 'confirmed', 'reversed', 'paid'].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={settle} data-testid="settle-btn"><Banknote className="w-4 h-4 mr-2" />Mark Settled</Button>
          <Button variant="outline" onClick={() => exportFile('csv')} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-2" />CSV</Button>
          <Button variant="outline" onClick={() => exportFile('xlsx')} data-testid="export-xlsx-btn"><Download className="w-4 h-4 mr-2" />Excel</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
              <tr><th className="px-4 py-3">Commission</th><th className="px-4 py-3">Order</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Base Fee</th><th className="px-4 py-3">Merchant Fee</th><th className="px-4 py-3">Commission</th><th className="px-4 py-3">Status</th></tr>
            </thead>
            <tbody>
              {data.commissions.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No commissions.</td></tr>}
              {data.commissions.map((c) => (
                <tr key={c.id} className="border-t" data-testid={`commission-row-${c.commission_code}`}>
                  <td className="px-4 py-3 font-mono">{c.commission_code}</td>
                  <td className="px-4 py-3 font-mono">{c.order_id || '—'}</td>
                  <td className="px-4 py-3 font-mono">{inr(c.transaction_amount)}</td>
                  <td className="px-4 py-3 font-mono">{inr(c.awadhpay_base_fee)}</td>
                  <td className="px-4 py-3 font-mono">{inr(c.merchant_fee)}</td>
                  <td className={`px-4 py-3 font-mono font-semibold ${c.commission_amount < 0 ? 'text-red-500' : 'text-green-600'}`}>{inr(c.commission_amount)}</td>
                  <td className="px-4 py-3"><Badge variant={c.status === 'paid' ? 'default' : c.status === 'reversed' ? 'destructive' : 'secondary'}>{c.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>)}
        </CardContent>
      </Card>
    </div>
  );
}
